import { useState, useEffect, useCallback } from 'react';
import prepareProducts from '../prepareProducts';
import { dataLayer } from '../../../../../helpers';
import { updateHistory } from '../../../PartnerAssetList/helpers';

const useProductFilter = ({ rawProducts, filters }) => {
    const [products, setProducts] = useState(prepareProducts(rawProducts));
    const [productFilters, setProductFilters] = useState(filters);
    const [visibleOptions, setVisibleOptions] = useState(() =>
        filters?.map((_, index) => index === 0) || []
    );
    const [activeFilters, setActiveFilters] = useState([]);

    useEffect(() => {
        if (filters) {
            setProductFilters(filters);
            setVisibleOptions(filters?.map((_, index) => index === 0));
        }
        setProducts(prepareProducts(rawProducts));
        setActiveFilters([]);
    }, [filters, rawProducts]);

    const toggleFilterVisibility = useCallback(index => {
        setVisibleOptions(prev => prev.map((visible, i) => (i === index ? !visible : visible)));
    }, []);

    const prepareNewFilters = useCallback((option, prevFilters) => {
        const { selectedOption, filterName, filterTitle } = option;
        const existingFilterIndex = prevFilters.findIndex(
            filter => filter.option === selectedOption && filter.name === filterName
        );

        if (existingFilterIndex >= 0) {
            return prevFilters.filter((_, index) => index !== existingFilterIndex);
        } else {
            return [
                ...prevFilters,
                {
                    name: filterName,
                    option: selectedOption,
                    attributeId: filterTitle,
                },
            ];
        }
    }, []);

    function replaceScreenSizeRangeFilters(availableFilters, newScreenSizeRangeFilter) {
        return availableFilters.map(filter => {
            if (filter.filterKey === 'screenSizeRange') {
                return newScreenSizeRangeFilter;
            }

            return filter;
        });
    }

    const getFilteredProductFilters = useCallback(
        (newFilters, productsToCheck, filterName, currentlyAvailableFilters) => {
            if (newFilters.length === 0) {
                return filters;
            }

            const availableFilters = filters.map(filter => {
                const { options, filterKey } = filter;

                const availableOptions = options.filter(option => {
                    const isSelected = newFilters.some(
                        selectedFilter => selectedFilter.name === filterKey && selectedFilter.option === option
                    );

                    if (isSelected) {
                        return true;
                    }

                    const hasFilterPotential = productsToCheck.some(product => {
                        return product.filterFields[filterKey]?.includes(option);
                    });

                    return hasFilterPotential;
                });

                return { ...filter, options: availableOptions };
            });

            if (newFilters.some(item => item.name === 'screenSizeRange')) {
                const defaultScreenSizeFilters = productFilters.find(
                    f => f.filterKey === 'screenSizeRange'
                );

                return replaceScreenSizeRangeFilters(availableFilters, defaultScreenSizeFilters);
            }

            if (filterName === 'screenSizeRange') {
                const originalScreenSizeFilters = currentlyAvailableFilters.find(
                    f => f.filterKey === 'screenSizeRange'
                );

                const isRemovingScreenSizeRange = !newFilters.some(filter => filter.name === 'screenSizeRange');

                if (isRemovingScreenSizeRange) {
                    return availableFilters;
                }

                // If we're selecting a screen size range, we don't filter away the other screen sizes
                return replaceScreenSizeRangeFilters(availableFilters, originalScreenSizeFilters);
            }

            return availableFilters;
        },
        [rawProducts, filters]
    );

    const filterProducts = useCallback(
        (selectedOption, filterName, filterTitle) => {
            setActiveFilters(prevFilters => {
                const newFilters = prepareNewFilters({ selectedOption, filterName, filterTitle }, prevFilters);
                const filteredProducts = rawProducts.filter(product => {
                    return newFilters.every(filter => {
                        // If filtering by screen size, return results that match any selected screen size
                        if (filter.name === 'screenSizeRange') {
                            return newFilters
                                .filter(f => f.name === 'screenSizeRange')
                                .some(screenFilter =>
                                    product.filterFields.screenSizeRange?.includes(screenFilter.option)
                                );
                        }
                        // Otherwise, return results that match all selected filters
                        return product.filterFields[filter.name]?.includes(filter.option);
                    });
                });

                setProducts(prepareProducts(filteredProducts));

                setProductFilters(prevFilters => {
                    const updatedFilters = getFilteredProductFilters(
                        newFilters,
                        filteredProducts,
                        filterName,
                        prevFilters
                    );
                    setProductFilters(updatedFilters);
                });

                dataLayer.triggerEvent('filters_updated', {
                    selectedFilters: newFilters,
                });

                return newFilters;
            });
        },
        [rawProducts, prepareNewFilters, getFilteredProductFilters]
    );

    const getMobileFilteredProductFilters = useCallback(
        (newFilters, previousFilters) => {
            if (newFilters.length === 0) {
                return filters;
            }

            // Get products that match all selected filters
            const filteredProducts = rawProducts.filter(product => {
                return newFilters.every(filter => {
                    if (filter.name === 'screenSizeRange') {
                        return newFilters
                            .filter(f => f.name === 'screenSizeRange')
                            .some(screenFilter => product.filterFields.screenSizeRange?.includes(screenFilter.option));
                    }
                    return product.filterFields[filter.name]?.includes(filter.option);
                });
            });


            // Get available filters based on the selected filters and the filtered products
            const availableFilters = filters.map(filter => {
                const { options, filterKey } = filter;

                const availableOptions = options.filter(option => {
                    const isSelected = newFilters.some(
                        selectedFilter => selectedFilter.name === filterKey && selectedFilter.option === option
                    );

                    if (isSelected) {
                        return true;
                    }

                    const hasFilterPotential = filteredProducts.some(product => {
                        return product.filterFields[filterKey]?.includes(option);
                    });

                    return hasFilterPotential;
                });

                return { ...filter, options: availableOptions };
            });

            if (newFilters.some(filter => filter.name === 'screenSizeRange')) {
                const originalScreenSizeFilters = previousFilters.find(f => f.filterKey === 'screenSizeRange');
                return replaceScreenSizeRangeFilters(availableFilters, originalScreenSizeFilters);
            }

            return availableFilters;
        },
        [rawProducts, filters]
    );

    // Function to apply filters in mobile view
    const mobileFilterProducts = useCallback(
        (newFilters = []) => {
            const groupedFilters = newFilters.reduce((acc, filter) => {
                acc[filter.name] = acc[filter.name] || [];
                acc[filter.name].push(filter.option);
                return acc;
            }, {});

            const filteredProducts = rawProducts.filter(product => {
                return Object.entries(groupedFilters).every(([filterName, options]) => {
                    const productValue = product.filterFields[filterName];
                    if (Array.isArray(productValue)) {
                        return options.some(option => productValue.includes(option));
                    } else {
                        return options.includes(productValue);
                    }
                });
            });

            // Update state with the filtered products
            setProducts(prepareProducts(filteredProducts));
            setActiveFilters(newFilters);
            dataLayer.triggerEvent('filters_updated', { selectedFilters: newFilters });
            return filteredProducts;
        },
        [rawProducts, prepareProducts, setProducts, setActiveFilters]
    );

    const clearFilters = useCallback(
        location => {
            setActiveFilters([]);
            setProducts(prepareProducts(rawProducts));
            setProductFilters(filters);
            updateHistory('', { location });
        },
        [rawProducts, filters]
    );

    return {
        products,
        productFilters,
        visibleOptions,
        activeFilters,
        toggleFilterVisibility,
        filterProducts,
        clearFilters,
        isFilterOptionChecked: useCallback(option => activeFilters.some(filter => filter.option === option), [
            activeFilters,
        ]),
        setActiveFilters,
        setProductFilters,
        getFilteredProductFilters,
        getMobileFilteredProductFilters,
        mobileFilterProducts,
        setProducts,
    };
};

export default useProductFilter;
